import { Injectable } from '@angular/core';
import { groupBy, uniq } from 'lodash';
import { throwError, of, forkJoin, Observable } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import {
  BackendService,
  AnalyticsApiService,
  Schema,
  TrackingService,
  DynamicRange,
  MessageService,
  ObjectBaseService,
  RequestOptionsArgs,
  GlobalMigrationService,
  Dataset,
  QueryService,
  IQueryFilterWrapper,
  QueryFilter,
} from '@hub-components';
import { Audit } from 'app/apps/dm/app/models/audit.model';
import { Report, ReportJson } from '../models/report.model';
import { ReportsResponse } from '../models/reports-response.model';

@Injectable({
  providedIn: 'root',
})
export class ReportsService extends ObjectBaseService {
  constructor(
    private backendService: BackendService,
    private analyticsApi: AnalyticsApiService,
    private trackingService: TrackingService,
    private messageService: MessageService,
    private globalMigrationService: GlobalMigrationService,
    private queryService: QueryService,
  ) {
    super();
  }

  private DEFAULT_NAME = 'New Report';

  public listReports(options: RequestOptionsArgs = {}): Observable<ReportsResponse> {
    return this.backendService
      .load('/reports', options)
      .pipe(
        map((response: { reports: ReportJson[], reports_count: number }) => {
          return {
            reports: response.reports.map(report => new Report(report)),
            reportsCount: response.reports_count,
          };
        }),
      );
  }

  public listMyReports(options: RequestOptionsArgs = {}): Observable<ReportsResponse> {
    return this.backendService
      .load('reports/my_reports', options)
      .pipe(
        map((response: { reports: ReportJson[], reports_count: number }) => {
          return {
            reports: response.reports.map(report => new Report(report)),
            reportsCount: response.reports_count,
          };
        }),
      );
  }

  public listSharedReports(options: RequestOptionsArgs = {}): Observable<ReportsResponse> {
    return this.backendService
      .load('reports/shared_reports', options)
      .pipe(
        map((response: { reports: ReportJson[], reports_count: number }) => {
          return {
            reports: response.reports.map(report => new Report(report)),
            reportsCount: response.reports_count,
          };
        }),
      );
  }

  public getAudits(offset: number, limit: number, reportId?: string): Observable<{ audits: Audit[], total: number }> {
    const params: any = { offset, limit };

    let url = '/reports/audits';
    if (reportId) {
      url = `/reports/${reportId}/audits`;
    }

    return this.backendService.load(url, { skipLoader: true, params })
      .pipe(
        map(response => (
          {
            audits: response.audits.map(audit => new Audit(audit)),
            total: response.total,
          })),
      );
  }

  public restore(id: string) {
    return this.backendService.create(`/reports/audits/${id}/restore`, {});
  }

  public newReport() {
    const dynamic: DynamicRange = { title: 'Last 7 Days', name: 'last', num: 7, unit: 'day' };
    return new Report({
      name: this.DEFAULT_NAME,
      settings: {
        interval: {
          type: 'dynamic',
          dynamic: dynamic,
        },
      },
      tables: [],
    });
  }

  public loadReport(reportId) {
    this.trackingService.track('view report', { reportId: reportId });
    return this.backendService.load(`/reports/${reportId}`)
      .pipe(
        mergeMap(rawReport => this.parseReport(rawReport)),
      );
  }

  public loadSharedReport(shareId: number) {
    this.trackingService.track('view shared report', { shareId: shareId });
    return this.backendService.load(`shared_reports/${shareId}`)
      .pipe(
        mergeMap(rawReport => this.parseReport(rawReport)),
      );
  }

  public loadPublicReport(uuid: string, password?: string) {
    if (password) {
      this.trackingService.track('view password protected public report', { publicId: uuid });
    } else {
      this.trackingService.track('view public report', { publicId: uuid });
    }

    return this.backendService.create(`s/${uuid}/show`, { password }, { throwErrors: true })
      .pipe(
        mergeMap(rawReport => this.parseReport(rawReport)),
      );
  }

  public create(report: Report, options: RequestOptionsArgs = {}) {
    const payload = report.asJson();
    return this.backendService.create('/reports', { report: payload }, options)
      .pipe(
        mergeMap((rawReport: ReportJson) => this.parseReport(rawReport)),
      );
  }

  public update(report: Report, options: RequestOptionsArgs = {}) {
    const payload = (report instanceof Report) ? report.asJson() : report;
    return this.backendService.update(`/reports/${report.id}`, { report: payload }, options)
      .pipe(mergeMap(rawReport => this.parseReport(rawReport)));
  }

  public renameReport(reportId, newName, options: RequestOptionsArgs = {}) {
    return this.backendService.update(`/reports/${reportId}/rename`, { name: newName }, options);
  }

  public updateReportFolder(reportId: string, reportFolderId: number | null) {
    return this.backendService.update(`/reports/${reportId}/folder`, {
      report: { report_folder_id: reportFolderId },
    }, { skipLoader: true });
  }

  public loadReportOwner(reportId) {
    return this.backendService.load(`/reports/${reportId}/owner`);
  }

  public changeOwner(reportId, newOwner) {
    return this.backendService.update(`/reports/${reportId}/change_owner`, { owner: newOwner });
  }

  public deleteReport(reportId) {
    this.trackingService.track('delete report', { reportId: reportId });
    return this.backendService.delete(`/reports/${reportId}`);
  }

  public exportReport(report: Report, isPublic = false, publicUuid?: string, includeInfoInExport?: boolean, excelTemplate?: string, password?: string) {
    const reportJson = report.asJson();
    const payload = JSON.stringify({
      name: reportJson.name,
      tables: reportJson.tables,
      global_filters: reportJson.global_filters,
    });
    const url = isPublic && publicUuid ? `s/${publicUuid}/excel` : '/excel';
    return this.backendService.create(url, {
      report: payload,
      include_info_in_export: includeInfoInExport,
      excel_template: excelTemplate,
      password: password,
    });
  }

  public accountUsers() {
    return this.backendService.load('/account_users');
  }

  public appUsers() {
    return this.backendService.load('/app_users', { skipLoader: true, cache: true });
  }

  public appSuites() {
    return this.backendService.load('/app_suites', { skipLoader: true, cache: true });
  }

  public parseReport(rawReport: ReportJson) {
    const tableDatasets = rawReport?.tables?.map(t => t.query.dataset) || [];
    const globalFiltersDatasets = rawReport?.global_filters?.data?.map(gf => gf.dataset) || [];
    const uniqueDatasets = uniq(tableDatasets.concat(globalFiltersDatasets));

    return forkJoin(
      uniqueDatasets.map(dataset => {
        return this.analyticsApi.loadSchema(dataset, { cache: true, throwErrors: true })
          .pipe(
            catchError(error => {
              if (error.status === 404) {
                return of(
                  new Schema({
                    meta: { display: { name: `Missing dataset ${dataset}` } },
                    dimensions: [],
                    metric_sets: [],
                  }),
                );
              } else if (error.status === 401) {
                this.trackingService.track('Unauthorized Error', { value: 'reports service' });
                this.messageService.unauthorized({ xRayId: error.headers.get('X-Amzn-Trace-Id') });
                return throwError(error);
              } else if (error.status === 403) {
                this.trackingService.track('Forbidden Error', { value: 'reports service' });
                this.messageService.forbidden({ xRayId: error.headers.get('X-Amzn-Trace-Id') });
                return throwError(error);
              } else {
                return throwError(error);
              }
            }),
          );
      }),
    ).pipe(map((schemas: Schema[]) => {
      const schemasByDataset = uniqueDatasets.reduce((acc, dataset, idx) => {
        acc[dataset] = schemas[idx];
        return acc;
      }, {} as Record<string, Schema>);

      let report = new Report(rawReport, schemasByDataset);
      if (report.settings.legacyInterval) {
        const dynamic: DynamicRange = { name: 'last', num: 7, unit: 'day' };
        report.settings.interval = {
          type: 'dynamic',
          dynamic: dynamic,
        };
        this.messageService.info('The date format for this report is no longer supported. Please save the report with a new date range.');
        setTimeout(() => {
          this.messageService.hide();
        }, 6000);
      }
      return report;
    }));
  }

  public loadDatasets(): Observable<Dataset[]> {
    return this.analyticsApi.loadDatasets({ cache: true, skipLoader: true })
      .pipe(
        map(datasets => {
          const groupedByType = groupBy(datasets, 'type');
          const managedByBurt = groupedByType['managed_by_burt'] || [];
          const managedByCustomer = groupedByType['managed_by_customer'] || [];

          if (managedByBurt.length) {
            managedByBurt[0]['optgroup'] = 'Managed Datasets';
          }

          return managedByCustomer.concat(managedByBurt);
        }),
      );
  }

  public checkGlobalFiltersMigration(report: Report): void {
    const data = this.globalMigrationService.getQueryMigrationData(report.globalFilters);

    if (this.globalMigrationService.currentVersion > data.version || data.version === 0) {
      const migratedData = this.globalMigrationService.migrate(data);
      report.globalFilters = {
        version: migratedData.finalVersion,
        data: migratedData.finalData || [],
      };
    }
  }

  public updateGlobalFiltersInTables(report: Report): void {
    report.tables.forEach(table => {
      table.query.globalFilters = this.globalFiltersForDataset(report, table.query.dataset);
    });
  }

  public updateGlobalFiltersInTable(report: Report, index: number): void {
    const table = report.tables[index];
    table.query.globalFilters = this.globalFiltersForDataset(report, table.query.dataset);
  }

  private globalFiltersForDataset(report: Report, dataset: string) {
    return report.globalFilters.data.filter(gf => gf.dataset === dataset);
  }

  public setFilterDisplayNames(report: Report, datasets: Dataset[]): void {
    report.globalFilters.data.forEach((filter: IQueryFilterWrapper) => {
      this.setDisplayNamesForFilter(filter, report, datasets);
    });
  }

  private setDisplayNamesForFilter(filter: IQueryFilterWrapper, report: Report, datasets: Dataset[]): void {
    const currentDataset = datasets?.find(dataset => dataset.name === filter.dataset);
    const dimensions = report.schemas[filter.dataset]?.['dimensions'];

    if (!currentDataset || !dimensions?.length) {
      return;
    }

    filter.datasetDisplayName = currentDataset.displayName;
    filter.expressions.forEach((expression: QueryFilter) => {
      const filterSourceList = this.queryService.getFilterSourceList(dimensions);
      if (!expression.key.includes('.')) {
        expression.key = `${expression.key}.${expression.key}`;
      }
      const dimensionName = this.queryService.findFilterSource(expression, filterSourceList);
      expression.displayName = dimensionName?.displayName;
    });
  }
}
